<script setup>
import {onMounted, ref} from "vue";
import GlobusDropdownList from "@/components/UI/GlobusDropdownList.vue";
import GlobusDropdownObject from "@/components/UI/GlobusDropdownObject.vue";
import GlobusRadio from "@/components/UI/GlobusRadio.vue";
import {
  dayNameListMock,
  generateTimeIntervals,
  timeNameListMock,
  updateFrequencyEveryMock
} from "@/composables/frequencyData";


const props = defineProps({
  modelValue: {
    type: [String, null],
    required: true,
    default: null
  }
});

const emit = defineEmits(["update:modelValue"]);

onMounted(() => {
  if (props.modelValue) {
    parseCronString(props.modelValue)
  } else {
    updateCronString()
  }
})

// ***** Переключатель выбора расписание/каждый n-минут *****
const setUpdateFrequencyType = (type) => {
  updateFrequencyType.value = type;
  updateCronString();
};
const updateFrequencyType = ref("every");

const cronString = ref("");
const updateCronString = () => {
  // Если выбран параметр "Каждые N-времени"
  if (updateFrequencyType.value === "every") {
    if (updateFrequencyEveryType.value.value === "minutes") {
      cronString.value = `0 0/${updateFrequencyEveryValue.value} * 1/1 * ? *`;
    } else if (updateFrequencyEveryType.value.value === "hours") {
      cronString.value = `0 0 0/${updateFrequencyEveryValue.value} 1/1 * ? *`;
    } else if (updateFrequencyEveryType.value.value === "days") {
      cronString.value = `0 0 0 1/${updateFrequencyEveryValue.value} * ?`;
    }
  }
  // Если выбран параметр "Расписание"
  else {
    const days = [];

    for (const day of updateFrequencySchedulerDays.value) {
      days.push(day.value);
    }
    cronString.value = `0 ${updateFrequencySchedulerDays.value[0].time.split(":")[1]}
     ${updateFrequencySchedulerDays.value[0].time.split(":")[0]} ? * ${days.join(",")} *`;
  }

  emit("update:modelValue", cronString);
};

// ***** ============= *****

// ***** Выбор каждые N минут/дней/часов *****

const timeNameList = timeNameListMock;

const updateFrequencyEvery = updateFrequencyEveryMock;

const setUpdateFrequencyEveryType = (type) => {
  updateFrequencyEveryType.value = type;
  updateFrequencyEveryValue.value = updateFrequencyEvery[type.value][0];
  updateCronString();
};

const setUpdateFrequencyEveryValue = (value) => {
  updateFrequencyEveryValue.value = value;
  updateCronString();
};

const updateFrequencyEveryType = ref({
  value: "minutes",
  name: "минуты"
});
const updateFrequencyEveryValue = ref(1);

// ***** ============= *****

// ***** Выбор по расписанию *****

const dayNameList = dayNameListMock;

const setUpdateFrequencySchedulerDay = (value, index) => {
  updateFrequencySchedulerDays.value[index].value = value.value;
  updateFrequencySchedulerDays.value[index].name = value.name;
  updateCronString();
};

const addFrequencySchedulerDay = () => {
  if (updateFrequencyType.value === "scheduler" && updateFrequencySchedulerDays.value.length < 7) {
    updateFrequencySchedulerDays.value.push({
      value: "MON",
      name: "ПН",
      time: "00:00"
    });
  }
  updateCronString();
};

const updateFrequencySchedulerDays = ref([
  {
    value: "MON",
    name: "ПН",
    time: "00:00"
  }
]);

const timeSchedulerNameList = generateTimeIntervals();

const setUpdateFrequencySchedulerTime = (value, index) => {
  updateFrequencySchedulerDays.value[index].time = value;
  updateCronString();
};

// ***** ============= *****

function parseCronString(cron) {
  // режим «каждые»
  let m = /^0 0\/(\d+) \* 1\/1 \* \? \*$/.exec(cron);
  if (m) {
    updateFrequencyType.value = "every";
    updateFrequencyEveryType.value = { value: "minutes", name: "минуты" };
    updateFrequencyEveryValue.value = Number(m[1]);
    return;
  }

  m = /^0 0 0\/(\d+) 1\/1 \* \? \*$/.exec(cron);
  if (m) {
    updateFrequencyType.value = "every";
    updateFrequencyEveryType.value = { value: "hours", name: "часы" };
    updateFrequencyEveryValue.value = Number(m[1]);
    return;
  }

  m = /^0 0 0 1\/(\d+) \* \?$/.exec(cron);
  if (m) {
    updateFrequencyType.value = "every";
    updateFrequencyEveryType.value = { value: "days", name: "дни" };
    updateFrequencyEveryValue.value = Number(m[1]);
    return;
  }

  // режим «по расписанию»
  m = /^0\s+(\d{1,2})\s+(\d{1,2})\s+\?\s+\*\s+([A-Z,]+)\s+\*$/.exec(cron);
  if (m) {
    updateFrequencyType.value = "scheduler";

    const [, mm, hh, days] = m;
    const parts = days.split(",");
    updateFrequencySchedulerDays.value = parts.map(d => ({
      value: d,
      name: dayNameListMock.find(x => x.value === d)?.name || d,
      time: hh.padStart(2, "0") + ":" + mm.padStart(2, "0")
    }));
    return;
  }

  // если ни под что не попало — оставляем defaults
}


</script>

<template>
  <div class="update-frequency">
    <div class="update-frequency__title required-label">
      Частота обновления источника
    </div>
    <div class="update-frequency__container">
      <div class="update-frequency__group">
        <div class="update-frequency__group-label">
          <GlobusRadio
            :model-value="updateFrequencyType === 'every'"
            @click="setUpdateFrequencyType('every')"
          />
          Каждые
        </div>
        <div class="update-frequency__group-controls">
          <GlobusDropdownList
            :disabled="updateFrequencyType !== 'every'"
            width="64px"
            :items="updateFrequencyEvery[updateFrequencyEveryType.value]"
            :model-value="updateFrequencyEveryValue"
            @update:model-value="val => setUpdateFrequencyEveryValue(val)"
          />
          <GlobusDropdownObject
            :disabled="updateFrequencyType !== 'every'"
            width="107px"
            :items="timeNameList"
            :model-value="updateFrequencyEveryType"
            @update:model-value="val => setUpdateFrequencyEveryType(val)"
          />
        </div>
      </div>
      <div class="update-frequency__group">
        <div class="update-frequency__group-label">
          <GlobusRadio
            :model-value="updateFrequencyType === 'scheduler'"
            @click="setUpdateFrequencyType('scheduler')"
          />
          Расписание
        </div>
        <div class="update-frequency__group-list">
          <div
            v-for="(day, index) in updateFrequencySchedulerDays"
            :key="index"
            class="update-frequency__group-controls"
          >
            <GlobusDropdownObject
              :disabled="updateFrequencyType !== 'scheduler'"
              width="64px"
              :items="dayNameList"
              :model-value="updateFrequencySchedulerDays[index]"
              @update:model-value="val => setUpdateFrequencySchedulerDay(val, index)"
            />
            <GlobusDropdownList
              :disabled="updateFrequencyType !== 'scheduler'"
              width="107px"
              :items="timeSchedulerNameList"
              :model-value="updateFrequencySchedulerDays[index].time"
              @update:model-value="val => setUpdateFrequencySchedulerTime(val, index)"
            />
            <button
              :class="{'button--disabled': updateFrequencyType !== 'scheduler'}"
              class="button"
              @click="addFrequencySchedulerDay"
            >
              + Добавить день
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped lang="scss">
@use '@/assets/styles/vars.scss';

.update-frequency {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__group {
    //display: flex;
    display: grid;
    grid-template-columns: 1fr 2fr;
    align-items: center;
    gap: 20px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: #7B87A5;
  }

  &__group-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__group-label {
    display: flex;
    align-items: center;
    justify-self: flex-start;
    align-self: flex-start;
    height: 38px;
    gap: 6px;
    font-size: 14px;
    font-weight: 400;
  }

  &__group-controls {
    display: flex;
    gap: 10px;
  }
}

.button {
  border: 1px solid #DEDEDE;
  color: #373D3F;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: 8px;

  &--disabled {
    cursor: not-allowed;
    background: #F7F8FD;
    border: 1px solid #F7F8FD;
    color: #DEDEDE;
  }
}

</style>